import React from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, getApiErrorMessage } from "@/services/api/client";
import { useAuthStore } from "@/stores/auth/auth-store";
import { canTriageFeedback } from "@/utils";

type FeedbackType = "bug" | "feature" | "general";
type FeedbackStatus = "new" | "read" | "resolved";

interface FeedbackUser {
  email?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
}

interface FeedbackSubmission {
  _id?: string;
  id?: string;
  userId?: string | FeedbackUser;
  type: FeedbackType;
  message: string;
  url?: string;
  status: FeedbackStatus;
  createdAt: string;
}

const STATUS_OPTIONS: FeedbackStatus[] = ["new", "read", "resolved"];

function getFeedbackId(item: FeedbackSubmission): string {
  return item.id || item._id || "";
}

function getUserEmail(user: FeedbackSubmission["userId"]): string {
  if (typeof user === "object" && user?.email) {
    return user.email;
  }
  return "Unknown";
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function typeVariant(type: FeedbackType): "destructive" | "info" | "secondary" {
  if (type === "bug") return "destructive";
  if (type === "feature") return "info";
  return "secondary";
}

function statusVariant(status: FeedbackStatus): "default" | "warning" | "success" {
  if (status === "new") return "warning";
  if (status === "resolved") return "success";
  return "default";
}

function parseFeedbackStatus(value: string): FeedbackStatus | null {
  return STATUS_OPTIONS.find((status) => status === value) ?? null;
}

export const FeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isPlatformAdmin = canTriageFeedback(user);

  const [items, setItems] = React.useState<FeedbackSubmission[]>([]);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (user && !isPlatformAdmin) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, isPlatformAdmin, navigate]);

  React.useEffect(() => {
    if (!isPlatformAdmin) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    api
      .get<FeedbackSubmission[]>("/feedback")
      .then((response) => {
        if (!cancelled) {
          setItems(response.data ?? []);
        }
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setError(getApiErrorMessage(fetchError, "Feedback could not be loaded."));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isPlatformAdmin]);

  const handleStatusChange = async (id: string, status: FeedbackStatus) => {
    const previousItems = items;
    setItems((current) =>
      current.map((item) => (getFeedbackId(item) === id ? { ...item, status } : item)),
    );

    try {
      const response = await api.patch<FeedbackSubmission, { status: FeedbackStatus }>(
        `/feedback/${id}/status`,
        { status },
      );
      const updatedFeedback = response.data;
      if (updatedFeedback) {
        setItems((current) =>
          current.map((item) => (getFeedbackId(item) === id ? updatedFeedback : item)),
        );
      }
    } catch (updateError) {
      setItems(previousItems);
      setError(getApiErrorMessage(updateError, "Status could not be updated."));
    }
  };

  if (!user || !isPlatformAdmin) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        Access denied. Platform admin privileges required.
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Feedback</h1>
          <p className="text-sm text-muted-foreground">Latest user submissions</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/admin")}>
          Admin
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border bg-background">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading feedback...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No feedback yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Message</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const id = getFeedbackId(item);
                  const expanded = expandedId === id;
                  return (
                    <tr key={id} className="border-b border-border last:border-b-0">
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {formatDate(item.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{getUserEmail(item.userId)}</td>
                      <td className="px-4 py-3">
                        <Badge variant={typeVariant(item.type)}>{item.type}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Badge variant={statusVariant(item.status)}>{item.status}</Badge>
                          <select
                            value={item.status}
                            onChange={(event) => {
                              const nextStatus = parseFeedbackStatus(event.target.value);
                              if (nextStatus) {
                                handleStatusChange(id, nextStatus);
                              }
                            }}
                            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                            aria-label="Update feedback status"
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="max-w-xl px-4 py-3">
                        <button
                          type="button"
                          className="block w-full text-left hover:text-primary"
                          onClick={() => setExpandedId(expanded ? null : id)}
                        >
                          <span className={expanded ? "whitespace-pre-wrap" : "line-clamp-2"}>
                            {item.message}
                          </span>
                          {item.url && expanded && (
                            <span className="mt-2 block break-all text-xs text-muted-foreground">
                              {item.url}
                            </span>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackPage;
